import React from "react";

import { CollectionProductCard } from "web/react/components/curated-collection/collection-product-card";
import { KeylineGrid, KeylineGridItem } from "web/react/components/keyline-grid";
import { MediaCard } from "web/react/emo/media-card";
import { useCuratedCollectionsAnalytics } from "web/react/hooks/use-curated-collection-analytics";
import { CuratedCollectionFeedSerializer } from "web/types/serializers";

interface CollectionGridItemProps {
    item: CuratedCollectionFeedSerializer;
    slug: string;
    sponsored: boolean;
    position: number;
    gender: "womenswear" | "menswear" | "mixed";
}

function CollectionGridItem({
    item,
    slug,
    sponsored,
    position,
    gender,
}: CollectionGridItemProps): React.ReactElement {
    const itemLinkId = item.product_card?.link_id;
    const itemProductId = item.product_card?.id;
    const itemUrl = item.media_card?.url;
    const label = item.media_card ? "media-card" : "product";
    const { containerRef, sendClickEvent } = useCuratedCollectionsAnalytics({
        label: label,
        curatedCollectionSlug: slug,
        isSponsored: sponsored,
        position,
        buyOptionId: itemLinkId?.toString(),
        productId: itemProductId?.toString(),
        url: itemUrl,
        gender: gender,
    });
    return (
        <KeylineGridItem colspan={item?.media_card?.columns || 1}>
            {item?.media_card && (
                <div ref={containerRef}>
                    <MediaCard
                        image={item.media_card.image}
                        url={item.media_card.url}
                        caption={item.media_card.caption}
                        sendClickEvent={sendClickEvent}
                    />
                </div>
            )}
            {item?.product_card && (
                <div ref={containerRef}>
                    <CollectionProductCard
                        product={item.product_card}
                        sponsored={sponsored}
                        sendClickEvent={sendClickEvent}
                    />
                </div>
            )}
        </KeylineGridItem>
    );
}

interface CollectionGridProps {
    feedItems: CuratedCollectionFeedSerializer[];
    slug: string;
    sponsored: boolean;
    gender: "womenswear" | "menswear" | "mixed";
}

export function CollectionGrid({
    feedItems,
    slug,
    gender,
    sponsored,
}: CollectionGridProps): React.ReactElement {
    const { containerRef } = useCuratedCollectionsAnalytics({
        label: "product",
        curatedCollectionSlug: slug,
        isSponsored: sponsored,
        gender: gender,
    });

    return (
        <div ref={containerRef}>
            <KeylineGrid>
                {feedItems.map((feedItem, i: number) => (
                    <CollectionGridItem
                        key={i}
                        position={i}
                        item={feedItem}
                        slug={slug}
                        sponsored={sponsored}
                        gender={gender}
                    />
                ))}
            </KeylineGrid>
        </div>
    );
}
